const authoredOverlayState = new WeakMap<object, { opacity: number }>();

const serializableChildren = (live: object): object[] => {
  const container = live as { getObjects?: () => object[] };
  return typeof container.getObjects === 'function'
    ? container.getObjects().filter((child) => !(child as { excludeFromExport?: boolean }).excludeFromExport)
    : [];
};

const hasAuthoredOverlayState = (live: object): boolean =>
  authoredOverlayState.has(live) || serializableChildren(live).some(hasAuthoredOverlayState);

export const normalizeAuthoredOverlaySerialization = (
  live: object,
  serialized: Record<string, unknown>,
): Record<string, unknown> => {
  const authored = authoredOverlayState.get(live);
  const normalized: Record<string, unknown> = authored
    ? { ...serialized, opacity: authored.opacity }
    : { ...serialized };
  const children = serializableChildren(live);
  if (!children.some(hasAuthoredOverlayState)) {
    return normalized;
  }
  const serializedChildren = serialized.objects;
  if (!Array.isArray(serializedChildren) || serializedChildren.length !== children.length) {
    throw new Error('Overlay serialization tree does not match live objects');
  }
  normalized.objects = children.map((child, index) => {
    const serializedChild: unknown = serializedChildren.at(index);
    if (!serializedChild || typeof serializedChild !== 'object' || Array.isArray(serializedChild)) {
      throw new Error('Invalid serialized overlay child');
    }
    return normalizeAuthoredOverlaySerialization(child, serializedChild as Record<string, unknown>);
  });
  return normalized;
};

type ToggleOverlayVisibilityProps = {
  shouldShowOverlay: boolean;
  fabricObject: fabric.Object;
  overlayElement: HTMLElement;
  textareaElement?: HTMLElement;
  elementDisplayType?: React.CSSProperties['display'];
  toggleProperty?: 'visible' | 'opacity';
};

export const toggleOverlayVisibility = ({
  shouldShowOverlay,
  fabricObject,
  overlayElement,
  textareaElement,
  elementDisplayType,
}: ToggleOverlayVisibilityProps) => {
  if (shouldShowOverlay) {
    if (!authoredOverlayState.has(fabricObject)) {
      authoredOverlayState.set(fabricObject, { opacity: fabricObject.opacity ?? 1 });
    }
    // set() (not assignment) so a parent group's render cache is invalidated.
    fabricObject.set({ opacity: 0 });
    overlayElement.style.display = elementDisplayType || 'block';
    if (textareaElement) {
      textareaElement.style.display = elementDisplayType || 'block';
    }
  } else {
    overlayElement.style.display = 'none';
    if (textareaElement) {
      textareaElement.style.display = 'none';
    }
    const authored = authoredOverlayState.get(fabricObject);
    if (authored) {
      fabricObject.set({ opacity: authored.opacity });
      authoredOverlayState.delete(fabricObject);
    }
  }
};
